import React from 'react';
import styled from 'styled-components';
import { 
  Smartphone, 
  Monitor, 
  Download, 
  X
} from 'lucide-react';
import { isIOS, isAndroid, isChrome, isSafari, isEdge, isFirefox } from '../utils/deviceDetect';

const InstructionsContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const InstructionsCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeviceSection = styled.div`
  margin-bottom: 24px;
`;

const DeviceTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImportantNote = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #92400e;
  
  strong {
    color: #78350f;
  }
`;

const StepList = styled.ol`
  margin: 0;
  padding-left: 20px;
  color: #4b5563;
  line-height: 1.6;
`;

const StepItem = styled.li`
  margin-bottom: 8px;
  
  strong {
    color: #1f2937;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #3b82f6;
  color: white;
  border-radius: 8px;
`;

const BrowserNote = styled.div`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
  font-size: 0.9rem;
  color: #6b7280;
`;

interface PWAInstallInstructionsProps {
  isOpen: boolean;
  onClose: () => void;
}

const PWAInstallInstructions: React.FC<PWAInstallInstructionsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const getDeviceInstructions = () => {
    if (isIOS) {
      return (
        <DeviceSection>
          <DeviceTitle>
            <Smartphone size={20} />
            iPhone/iPad
          </DeviceTitle>
          <StepList>
            <StepItem>
              <strong>Tap the Share button</strong> (square with arrow pointing up) at the bottom of Safari
            </StepItem>
            <StepItem>
              <strong>Scroll down and tap "Add to Home Screen"</strong>
            </StepItem>
            <StepItem>
              <strong>Tap "Add"</strong> to confirm installation as a proper PWA
            </StepItem>
          </StepList>
          <BrowserNote>
            <strong>Note:</strong> Make sure you're using Safari browser on iOS. Chrome and other browsers on iOS don't support PWA installation.
          </BrowserNote>
        </DeviceSection>
      );
    }

    if (isAndroid) {
      return (
        <DeviceSection>
          <DeviceTitle>
            <Smartphone size={20} />
            Android
          </DeviceTitle>
          <StepList>
            <StepItem>
              <strong>Look for the install banner</strong> that appears at the bottom of the screen
            </StepItem>
            <StepItem>
              <strong>Tap "Install"</strong> or "Add to Home Screen"
            </StepItem>
            <StepItem>
              <strong>Confirm installation</strong> when prompted to install as a proper PWA
            </StepItem>
          </StepList>
          <BrowserNote>
            <strong>Supported browsers:</strong> Chrome, Samsung Internet, Edge, Firefox
          </BrowserNote>
        </DeviceSection>
      );
    }

    // Desktop instructions
    return (
      <DeviceSection>
        <DeviceTitle>
          <Monitor size={20} />
          Desktop
        </DeviceTitle>
        <StepList>
          <StepItem>
            <strong>Look for the install icon</strong> in the address bar (Chrome/Edge) or menu (Firefox)
          </StepItem>
          <StepItem>
            <strong>Click the install icon</strong> or "Install FMS" button
          </StepItem>
          <StepItem>
            <strong>Click "Install"</strong> in the confirmation dialog
          </StepItem>
        </StepList>
        <BrowserNote>
          <strong>Supported browsers:</strong> Chrome, Edge, Firefox, Opera
        </BrowserNote>
      </DeviceSection>
    );
  };

  const getBrowserSpecificNote = () => {
    if (isChrome()) return "Chrome: Look for the install icon in the address bar";
    if (isEdge()) return "Edge: Look for the install icon in the address bar";
    if (isFirefox()) return "Firefox: Look for the install option in the menu";
    if (isSafari()) return "Safari: Use the Share button to add to home screen";
    return "Your browser supports PWA installation";
  };

  return (
    <InstructionsContainer onClick={onClose}>
      <InstructionsCard onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
        
        <Title>
          <IconWrapper>
            <Download size={20} />
          </IconWrapper>
          Install FMS App
        </Title>
        
        <ImportantNote>
          <strong>Important:</strong> This will install the app as a proper PWA (Progressive Web App), not just a shortcut. 
          The app will run in standalone mode with full functionality, offline capabilities, and native app-like experience.
        </ImportantNote>

        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Follow these steps to install the FMS app as a proper PWA on your device for a better experience:
        </p>

        {getDeviceInstructions()}

        <BrowserNote>
          <strong>Current browser:</strong> {getBrowserSpecificNote()}
        </BrowserNote>

        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#eff6ff', 
          border: '1px solid #bfdbfe', 
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: '#1e40af'
        }}>
          <strong>💡 Tip:</strong> After installation, you can access the app directly from your home screen or desktop, and it will work offline!
        </div>
      </InstructionsCard>
    </InstructionsContainer>
  );
};

export default PWAInstallInstructions;
